import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import checkVrcExperiencedEnabled from '~/helpers/checkVrcExperiencedEnabled';

const useUserActivity = (): { isUserActive: boolean; isUserTimedout: boolean } => {
  const { pageLoading } = useSelector((store: ReduxState) => store);
  const [isUserActive, setUserActive] = useState(false);
  const [isUserTimedout, setUserTimedout] = useState(false);
  const userActivityRef = useRef<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { vrcExperienceEnabled, vrcExperienceModalEnabled, vrcExperienceExitIntentEnabled, timeoutDuration } =
    checkVrcExperiencedEnabled();

  const resetTimer = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleUserActivity = () => {
    if (!isUserActive) {
      setUserActive(true);
      setUserTimedout(false);

      resetTimer();
      timeoutRef.current = setTimeout(() => {
        setUserActive(false);
        setUserTimedout(true);
      }, timeoutDuration);
    }

    cancelAnimationFrame(userActivityRef.current as number);

    let start: number | undefined;
    const animate = (timestamp: number) => {
      if (start === undefined) {
        start = timestamp;
      }

      const timeElapsed = timestamp - start;

      if (timeElapsed > timeoutDuration) {
        setUserActive(false);
        setUserTimedout(true);
      } else {
        userActivityRef.current = requestAnimationFrame(animate);
      }
    };

    userActivityRef.current = requestAnimationFrame(animate);

    setTimeout(() => {
      resetTimer();
    }, 3000); // Reset timer every 3 seconds
  };

  const handleMouseLeave = () => {
    setUserActive(false);
    setUserTimedout(true);
  };

  useEffect(() => {
    if (!isUserTimedout && !pageLoading && vrcExperienceModalEnabled) {
      document.addEventListener('mousemove', handleUserActivity);
      document.addEventListener('scroll', handleUserActivity);
      if (vrcExperienceExitIntentEnabled) {
        document.addEventListener('mouseleave', handleMouseLeave);
      }
    }

    return () => {
      document.removeEventListener('mousemove', handleUserActivity);
      document.removeEventListener('scroll', handleUserActivity);
      if (vrcExperienceExitIntentEnabled) {
        document.removeEventListener('mouseleave', handleMouseLeave);
      }
      cancelAnimationFrame(userActivityRef.current as number);
      resetTimer();
    };
  }, [isUserActive, isUserTimedout, vrcExperienceEnabled, pageLoading, vrcExperienceModalEnabled]);

  return {
    isUserActive,
    isUserTimedout,
  };
};

export default useUserActivity;
